import { Controller } from "react-hook-form";
import {
  FormControl,
  Box,
  FormHelperText,
  Select,
  MenuItem,
} from "@mui/material";
import {
  ConfigurationItemBase,
  ConfigurationList,
  ConfigurationPaper,
} from "~/components/configuration-items";
import { useSpaceSettingsForm } from "./hooks/SpaceSettingsForm";
import { useCanUpdateSpace } from "~/lib/iam/actions/mondoo/captain/Captain/UpdateSpace";
import { SpaceScope } from "~/hooks/useScope";
import { FieldLoadingIcon } from "~/components/Form/components/FieldLoadingIcon";
import { truncateText } from "~/lib/truncateText";

export type AssetSettingsProps = {
  space: SpaceScope;
};

export function AssetSettings({ space }: AssetSettingsProps) {
  const canUpdateSpace = useCanUpdateSpace(space);
  const { settings, form, onSubmit, fieldIsUpdating } = useSpaceSettingsForm({
    spaceMrn: space.mrn,
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isLoading },
  } = form;

  return (
    settings &&
    !isLoading && (
      <Box component="form">
        <ConfigurationPaper>
          <ConfigurationList title="Asset settings">
            <ConfigurationItemBase
              primary="Remove terminated assets"
              secondary="Automatically remove terminated assets."
            >
              <FormControl fullWidth>
                <Controller
                  name="terminatedAssets"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      onClose={handleSubmit(onSubmit)}
                      disabled={
                        !canUpdateSpace || fieldIsUpdating("terminatedAssets")
                      }
                      IconComponent={
                        fieldIsUpdating("terminatedAssets")
                          ? FieldLoadingIcon
                          : undefined
                      }
                    >
                      <MenuItem value={0}>Never</MenuItem>
                      <MenuItem value={1}>24 Hours</MenuItem>
                    </Select>
                  )}
                />
                {errors.terminatedAssets && (
                  <FormHelperText error>
                    {errors.terminatedAssets.message}
                  </FormHelperText>
                )}
              </FormControl>
            </ConfigurationItemBase>
            <ConfigurationItemBase
              primary="Remove inactive assets"
              secondary="Automatically remove assets that have not been scanned in the time specified."
            >
              <FormControl fullWidth>
                <Controller
                  name="inactiveAssets"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      onClose={handleSubmit(onSubmit)}
                      disabled={
                        !canUpdateSpace || fieldIsUpdating("inactiveAssets")
                      }
                      IconComponent={
                        fieldIsUpdating("inactiveAssets")
                          ? FieldLoadingIcon
                          : undefined
                      }
                    >
                      <MenuItem value={0}>Never</MenuItem>
                      <MenuItem value={1}>1 day</MenuItem>
                      <MenuItem value={3}>3 days</MenuItem>
                      <MenuItem value={7}>7 days</MenuItem>
                      <MenuItem value={14}>14 days</MenuItem>
                      <MenuItem value={30}>30 days</MenuItem>
                      <MenuItem value={60}>60 days</MenuItem>
                      <MenuItem value={90}>90 days</MenuItem>
                    </Select>
                  )}
                />
                {errors.inactiveAssets && (
                  <FormHelperText error>
                    {errors.inactiveAssets.message}
                  </FormHelperText>
                )}
              </FormControl>
            </ConfigurationItemBase>
            <ConfigurationItemBase
              primary="End-of-life asset warnings"
              secondary="Define whether (and how long in advance) Mondoo displays warnings about assets reaching the end of their supported lifecycle. This will also increase the assets' risk score."
            >
              <FormControl fullWidth>
                <Controller
                  name="eolAssets"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      onClose={handleSubmit(onSubmit)}
                      disabled={!canUpdateSpace || fieldIsUpdating("eolAssets")}
                      IconComponent={
                        fieldIsUpdating("eolAssets")
                          ? FieldLoadingIcon
                          : undefined
                      }
                    >
                      <MenuItem value={0}>Disable</MenuItem>
                      <MenuItem value={12}>1 year</MenuItem>
                      <MenuItem value={6}>6 months (default)</MenuItem>
                      <MenuItem value={3}>3 months</MenuItem>
                      <MenuItem value={1}>1 month</MenuItem>
                    </Select>
                  )}
                />
                {errors.eolAssets && (
                  <FormHelperText error>
                    {errors.eolAssets.message}
                  </FormHelperText>
                )}
              </FormControl>
            </ConfigurationItemBase>
            <ConfigurationItemBase
              primary="Vulnerability scanning"
              secondary="Enable or disable vulnerability scanning. Disabling this setting is not recommended and is for advanced users only."
            >
              <FormControl fullWidth>
                <Controller
                  name="vulnerabilityScanning"
                  control={control}
                  render={({ field }) => (
                    <Select
                      id="vulnerabilityScanning"
                      {...field}
                      onClose={handleSubmit(onSubmit)}
                      disabled={
                        !canUpdateSpace ||
                        fieldIsUpdating("vulnerabilityScanning")
                      }
                      IconComponent={
                        fieldIsUpdating("vulnerabilityScanning")
                          ? FieldLoadingIcon
                          : undefined
                      }
                    >
                      <MenuItem value="enabled">Enabled</MenuItem>
                      <MenuItem value="disabled">Disabled</MenuItem>
                    </Select>
                  )}
                />
                {errors.vulnerabilityScanning && (
                  <FormHelperText error>
                    {errors.vulnerabilityScanning.message}
                  </FormHelperText>
                )}
              </FormControl>
            </ConfigurationItemBase>
            <ConfigurationItemBase
              primary="Include MQL vulnerabilities"
              secondary="Enable or disable the vulnerability scoring based on MQL created vulnerabilities."
            >
              <FormControl fullWidth>
                <Controller
                  name="mqlVulnerabilities"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      onClose={handleSubmit(onSubmit)}
                      disabled={
                        !canUpdateSpace || fieldIsUpdating("mqlVulnerabilities")
                      }
                      IconComponent={
                        fieldIsUpdating("mqlVulnerabilities")
                          ? FieldLoadingIcon
                          : undefined
                      }
                    >
                      <MenuItem value="enabled">Enabled</MenuItem>
                      <MenuItem value="disabled">Disabled</MenuItem>
                    </Select>
                  )}
                />
                {errors.mqlVulnerabilities && (
                  <FormHelperText error>
                    {errors.mqlVulnerabilities.message}
                  </FormHelperText>
                )}
              </FormControl>
            </ConfigurationItemBase>
            <ConfigurationItemBase
              primary="Include experimental sources"
              secondary="Include vulnerability sources marked as experimental into the vulnerability scanning."
            >
              <FormControl fullWidth>
                <Controller
                  name="experimentalSources"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      onClose={handleSubmit(onSubmit)}
                      multiple
                      displayEmpty
                      renderValue={(values) => {
                        if (values.length === 0) {
                          return "Select sources to include";
                        }
                        return truncateText(
                          values
                            .map(
                              (id) =>
                                settings.mvdExperimentalSourcesConfiguration.sources?.find(
                                  (source) => id === source.id,
                                )?.name ?? id,
                            )
                            .join(", "),
                          25,
                        );
                      }}
                      disabled={
                        !canUpdateSpace ||
                        fieldIsUpdating("experimentalSources")
                      }
                      IconComponent={
                        fieldIsUpdating("experimentalSources")
                          ? FieldLoadingIcon
                          : undefined
                      }
                    >
                      {settings.mvdExperimentalSourcesConfiguration.sources?.map(
                        (source) => {
                          return (
                            <MenuItem key={source.id} value={source.id}>
                              {source.name}
                            </MenuItem>
                          );
                        },
                      )}
                    </Select>
                  )}
                />
                {errors.experimentalSources && (
                  <FormHelperText error>
                    {errors.experimentalSources.message}
                  </FormHelperText>
                )}
              </FormControl>
            </ConfigurationItemBase>
          </ConfigurationList>
        </ConfigurationPaper>
      </Box>
    )
  );
}
